// Session-based virtual filesystem for temporary files and directories
import { normalizePath } from './pathUtils'

export interface SessionNode {
  name: string
  type: 'file' | 'directory'
  content?: string // For files
  children?: SessionNode[] // For directories
  created: Date
  modified: Date
}

/**
 * Find a node in the session filesystem by path
 */
export function findSessionNode(
  sessionFs: SessionNode[],
  path: string
): SessionNode | null {
  if (!path || path === '~' || path === '') {
    return null // Root level
  }

  const normalizedPath = normalizePath(path)
  const parts = normalizedPath.split('/').filter(p => p)

  let current: SessionNode[] = sessionFs

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    const node = current.find(n => n.name === part)

    if (!node) {
      return null
    }

    if (i === parts.length - 1) {
      return node
    }

    if (node.type === 'directory' && node.children) {
      current = node.children
    } else {
      return null
    }
  }

  return null
}

/**
 * Get all entries in a directory (combining static and session)
 */
export function getSessionEntries(
  sessionFs: SessionNode[],
  path: string
): SessionNode[] {
  if (!path || path === '~' || path === '') {
    return sessionFs
  }

  const node = findSessionNode(sessionFs, path)
  if (node && node.type === 'directory') {
    return node.children || []
  }

  return []
}

/**
 * Add a file to the session filesystem
 */
export function addSessionFile(
  sessionFs: SessionNode[],
  path: string,
  filename: string,
  content: string = ''
): SessionNode[] {
  const newFs = structuredClone(sessionFs)

  // Normalize path: '~' or '~/' or '' should all result in empty string (root)
  const normalizedPath = normalizePath(path)
  const parts = normalizedPath.split('/').filter(p => p && p !== '~')

  let current: SessionNode[] = newFs

  // Navigate to the target directory
  for (const part of parts) {
    const node = current.find(n => n.name === part && n.type === 'directory')
    if (!node) {
      return sessionFs // Path doesn't exist
    }
    if (!node.children) {
      node.children = []
    }
    current = node.children
  }

  // Check if file already exists
  const existing = current.find(n => n.name === filename)
  if (existing) {
    // Update modification time
    existing.modified = new Date()
    if (content !== undefined) {
      existing.content = content
    }
  } else {
    // Add new file
    current.push({
      name: filename,
      type: 'file',
      content,
      created: new Date(),
      modified: new Date(),
    })
  }

  return newFs
}

/**
 * Add a directory to the session filesystem
 */
export function addSessionDirectory(
  sessionFs: SessionNode[],
  path: string,
  dirname: string
): SessionNode[] {
  const newFs = structuredClone(sessionFs)

  // Normalize path: '~' or '~/' or '' should all result in empty string (root)
  const normalizedPath = normalizePath(path)
  const parts = normalizedPath.split('/').filter(p => p && p !== '~')

  let current: SessionNode[] = newFs

  // Navigate to the target directory
  for (const part of parts) {
    const node = current.find(n => n.name === part && n.type === 'directory')
    if (!node) {
      return sessionFs // Path doesn't exist
    }
    if (!node.children) {
      node.children = []
    }
    current = node.children
  }

  // Check if directory already exists
  const existing = current.find(n => n.name === dirname)
  if (existing) {
    return sessionFs // Already exists
  }

  // Add new directory
  current.push({
    name: dirname,
    type: 'directory',
    children: [],
    created: new Date(),
    modified: new Date(),
  })

  return newFs
}

/**
 * Remove a file or directory from the session filesystem
 */
export function removeSessionNode(
  sessionFs: SessionNode[],
  path: string,
  name: string
): SessionNode[] {
  const newFs = structuredClone(sessionFs)

  const normalizedPath = normalizePath(path)
  const parts = normalizedPath.split('/').filter(p => p)

  let current: SessionNode[] = newFs

  // Navigate to the target directory
  for (const part of parts) {
    const node = current.find(n => n.name === part && n.type === 'directory')
    if (!node) {
      return sessionFs // Path doesn't exist
    }
    if (!node.children) {
      node.children = []
    }
    current = node.children
  }

  // Remove the node
  const index = current.findIndex(n => n.name === name)
  if (index !== -1) {
    current.splice(index, 1)
  }

  return newFs
}

/**
 * Check if a path exists in session filesystem
 */
export function sessionNodeExists(
  sessionFs: SessionNode[],
  path: string
): boolean {
  return findSessionNode(sessionFs, path) !== null
}

/**
 * Get file content from session filesystem
 */
export function getSessionFileContent(
  sessionFs: SessionNode[],
  path: string
): string | null {
  const node = findSessionNode(sessionFs, path)
  if (node && node.type === 'file') {
    return node.content || ''
  }
  return null
}
